import { useTranslation } from 'react-i18next';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface SeverityCounts {
  critical: number;
  error: number;
  warning: number;
  info: number;
}

interface ExceptionSeverityGridProps {
  counts: SeverityCounts;
  title?: string;
  className?: string;
}

const SEVERITIES: Array<{
  key: keyof SeverityCounts;
  tone: string;
  dot: string;
}> = [
  { key: 'critical', tone: 'text-efs-critical', dot: 'bg-efs-critical' },
  { key: 'error', tone: 'text-efs-error', dot: 'bg-efs-error' },
  { key: 'warning', tone: 'text-efs-warning', dot: 'bg-efs-warning' },
  { key: 'info', tone: 'text-efs-info', dot: 'bg-efs-info' },
];

/**
 * Four-up grid of exception counts by severity.
 * Used on the Dashboard to give a quick glance at batch health.
 */
export const ExceptionSeverityGrid = ({ counts, title, className }: ExceptionSeverityGridProps) => {
  const { t } = useTranslation();

  return (
    <Card className={cn('min-w-0', className)}>
      {title ? (
        <CardHeader className="pb-1 pt-3 px-4">
          <CardTitle className="text-xs font-medium text-efs-text-muted uppercase tracking-wide">
            {title}
          </CardTitle>
        </CardHeader>
      ) : null}
      <CardContent className={cn('px-4 pb-3', title ? '' : 'pt-3')}>
        <div className="grid grid-cols-4 gap-3">
          {SEVERITIES.map(({ key, tone, dot }) => (
            <div key={key} className="flex flex-col items-center gap-0.5">
              <span className={cn('text-xl font-semibold tabular-nums', tone)}>{counts[key]}</span>
              <span className="flex items-center gap-1 text-xs text-efs-text-muted">
                <span className={cn('inline-block h-2 w-2 rounded-full', dot)} />
                {t(`efs.common.status.${key}`, { defaultValue: key })}
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
